import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState
} from "react";
import { v4 } from "uuid";
import * as Sentry from "@sentry/react";
import { ApolloError } from "@apollo/client/errors";
import { TConductorInstance } from "react-canvas-confetti/src/types";
import {
  ConsultantPipelineCardFragment,
  ConsultantPipelineStatus,
  KanbanBoardType,
  useCardAddedSubscription,
  useCardDeletedSubscription,
  useCardMovedSubscription,
  useGetConsultantPipelineBoardQuery,
  useGetConsultantPipelineCardLazyQuery,
  useGetConsultantPipelineCardsQuery,
  useMoveCardMutation,
  useUnsetNextContactDateMutation,
  useGetAllConsultantTagsLazyQuery
} from "@bonliva-crm/graphql";
import { useSnackbars, useUserProfile } from "@hooks";
import { CardDescription, OnMoveCardParams } from "../kanban-board/kanban-types";
import {
  BoardAction,
  BoardState,
  consultantPipelineBoardReducer
} from "./consultant-pipeline-board-reducer";
import { UseSearchFilters, useSearchFilters } from "@bonliva-crm/ui/search-filters";
import { ConsultantFilterState } from "@consultants/consultants-search-new/utils/consultant-filter-state";

interface IConsultantPipelineBoardContext {
  state: BoardState;
  loading: boolean;
  error?: ApolloError;
  filterManager: UseSearchFilters<ConsultantFilterState>;

  dispatch: React.Dispatch<BoardAction>;
  refetch: () => void;
  refetchCards: () => void;
  setBoardUserId: (userId: string) => void;
  setUntagConsultantId: (consultantId: string) => void;
  setConsultantRelevantLaterId: (consultantId: string) => void;
  setFireworksConductor: (conductor: TConductorInstance) => void;
  onMoveCard: (params: OnMoveCardParams) => Promise<void>;
}

// Pipeline columns whose order is not user-controlled. The backend rejects
// same-column reorder for these (see ConsultantPipelineService.moveCard).
// Mirror the list here so the client doesn't even attempt the mutation.
const NON_SORTABLE_PIPELINE_COLUMNS: ReadonlySet<string> = new Set<string>([
  ConsultantPipelineStatus.RelevantInTheFuture,
  ConsultantPipelineStatus.NotRelevant
]);

export const defaultFilter: Partial<ConsultantFilterState> = {
  text: "",
  qualification: null,
  nationalQualification: null,
  tags: [],
  simpleProfession: {
    professions: [] as string[],
    areaOfExpertises: [] as string[]
  },
  regions: []
};

const ConsultantPipelineBoardContext =
  createContext<IConsultantPipelineBoardContext | null>(null);

export const useConsultantPipelineBoardContext = () => {
  const context = useContext(ConsultantPipelineBoardContext);
  if (context === null) {
    throw new Error(
      "useConsultantPipelineBoardContext must be used within ConsultantPipelineBoardContext"
    );
  }
  return context;
};

// How long after our own moveCard we keep ignoring incoming subscription
// patches for the same card. The patches arrive via the
// notifyOnConsultantUpdated republish (sessionId="") so they bypass our
// sessionId self-skip filter. Re-applying them on the originator is harmful
// because the patch only carries the moved card's new `order` — the other
// cards in that column still hold their pre-shift cached orders, and the
// reducer's sortBy ends up placing the moved card at the wrong position.
// See CRMDEV-6787 follow-up (Sami→Presenterad → drifts to end).
const RECENT_LOCAL_MOVE_WINDOW_MS = 6000;

export const ConsultantPipelineBoardContextProvider = ({ children }) => {
  const { showError } = useSnackbars();
  const user = useUserProfile();
  const sessionId = useRef<string>(v4());
  // cardId -> timestamp; tells patchSingleCard "this client just moved
  // this card, ignore the incoming patch".
  const recentLocalMoves = useRef<Map<string, number>>(new Map());
  const [fireworks, setFireworks] = useState<TConductorInstance | null>(null);

  // TEMP DIAGNOSTIC (CRMDEV-6787 follow-up): the "card disappears after
  // drop" bug reproduces intermittently even with the originator-skip
  // window and the optimistic-order fix in place. This helper records a
  // Sentry breadcrumb + console.debug every time a code path touches a
  // card that's currently within the recent-local-move window — so when
  // the user reproduces it we can see in the Sentry session replay /
  // breadcrumb trail exactly which dispatch erased the card. Once the
  // race is identified and patched, remove this and all `traceLocalMove`
  // call sites.
  const traceLocalMove = (
    source: string,
    cardId: string | null | undefined,
    extra?: Record<string, unknown>
  ) => {
    if (!cardId) return;
    const ts = recentLocalMoves.current.get(cardId);
    if (!ts) return; // only trace cards we just moved ourselves
    const ageMs = Date.now() - ts;
    const data = { cardId, ageMs, ...extra };
    // eslint-disable-next-line no-console
    console.debug(`[kanban-race] ${source}`, data);
    Sentry.addBreadcrumb({
      category: "kanban-race",
      level: "warning",
      message: source,
      data
    });
  };

  const [getTags, { loading: loadingTags, data: dataTags }] =
    useGetAllConsultantTagsLazyQuery({
      fetchPolicy: "cache-and-network"
    });

  const [state, dispatch] = useReducer(consultantPipelineBoardReducer, {
    board: null,
    initialBoard: null,
    lastActionType: null,
    filters: defaultFilter as ConsultantFilterState,
    filterIsDirty: false,
    boardUserId: user.id,
    untagConsultantId: null,
    untagConsultantQuestionText: "",
    setConsultantRelevantLaterId: null,
    pendingCards: null
  });

  // Mirror of `state` accessible from async callbacks (mutation `update`
  // callbacks fire long after the closure they were created in, by which
  // time the closure-captured `state` is stale). Used to read the
  // optimistic synthOrder set by Fix 1 in move-card.ts when writing to
  // the Apollo cache, so the post-mutation re-emit doesn't displace the
  // card based on its pre-move stale cached order.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Look up a card's current optimistic order in initialBoard (set by
  // Fix 1's synthOrder in move-card.ts). Used by the mutation `update`
  // callbacks to keep the Apollo cache's `order` field in sync with the
  // visual position; without this, the cache.modify(columnId) below
  // triggers a kanbanCards re-emit that sorts the card by its STALE
  // pre-move cached order — which lands it at the wrong slot (typically
  // the end of the column if the old order was high).
  const readOptimisticOrder = (cardId: string): number | undefined => {
    const board = stateRef.current.initialBoard;
    if (!board) return undefined;
    for (const col of board.columns) {
      const wrapper = col.cards.find((c) => c.id === cardId);
      if (wrapper && typeof wrapper.issue.order === "number") {
        return wrapper.issue.order;
      }
    }
    return undefined;
  };

  const filterManager = useSearchFilters({
    initialValues: defaultFilter,
    localStorageKey: "pipelineBoardSearchFavoriteFields",
    activeFilters: [
      // Kvalitetssäkringsdokument
      "qualification",

      // Kvalitetssäkring Nationella
      "nationalQualification",

      // Taggar
      "tags",

      // Yrkestitel
      "simpleProfession",

      // Intresserad av arbete i
      "regions"
    ],
    onChange: (value) => {
      dispatch({
        type: "FILTER_CHANGE",
        payload: {
          filter: value
        }
      });
    },
    overrides: {
      tags: {
        name: "tags",
        getOptions: () => {
          getTags();
        },
        tags: dataTags?.getAllConsultantTags?.tags ?? [],
        loading: loadingTags
      }
    }
  });

  // Board structure (columns) is small and rarely changes — cache-first avoids
  // a refetch on every revisit. Live updates arrive via subscriptions.
  // NOTE: Apollo Client 3.6 does not fire `onCompleted` for cache-hit results
  // on a fresh mount, so we dispatch from a useEffect watching `data` instead.
  const {
    data: boardData,
    error,
    loading,
    refetch
  } = useGetConsultantPipelineBoardQuery({
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
    variables: {
      ownerId: state.boardUserId
    }
  });

  useEffect(() => {
    if (!boardData) return;
    dispatch({
      type: state?.board ? "REFETCH_BOARD" : "INITIALIZE_BOARD",
      payload: { response: boardData }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardData]);

  // Fire in parallel with the board query. The reducer buffers cards if they
  // arrive before INITIALIZE_BOARD has run. See CRMDEV-6787.
  const { data: cardsData, refetch: refetchCardsQuery } =
    useGetConsultantPipelineCardsQuery({
      fetchPolicy: "cache-and-network",
      variables: {
        boardId: state.boardUserId
      }
    });

  useEffect(() => {
    if (!cardsData?.kanbanCards) return;
    const cards = cardsData.kanbanCards as ConsultantPipelineCardFragment[];
    // TEMP DIAGNOSTIC: detect when a cards-query re-emit during the
    // post-move window is going to wipe a card we just moved. If the
    // moved cardId is missing from the array entirely, LOAD_CARDS_ACTION
    // will rebuild `board` without it → user sees disappear.
    if (recentLocalMoves.current.size > 0) {
      const byId = new Set(cards.map((c) => c._id));
      for (const [cardId] of recentLocalMoves.current) {
        const incoming = cards.find((c) => c._id === cardId);
        traceLocalMove("LOAD_CARDS_ACTION:cardsData-emit", cardId, {
          present: byId.has(cardId),
          incomingColumnId: incoming?.columnId ?? null,
          incomingOrder: incoming?.order ?? null,
          totalCards: cards.length
        });
      }
    }
    dispatch({
      type: "LOAD_CARDS_ACTION",
      payload: { cards }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardsData]);

  const loadCards = () => {
    void refetchCardsQuery();
  };

  const [unsetNextContactDate] = useUnsetNextContactDateMutation();

  // Move card callback
  const [_moveCard] = useMoveCardMutation();

  const onMoveCard = async (params: OnMoveCardParams) => {
    // Non-sortable columns: same-column reorder is a no-op. "Aktuell längre
    // fram" is auto-sorted by next contact date (CRMDEV-2644) and "Ej aktuell"
    // doesn't expose user-driven ordering.
    if (
      params.sourceColumnName === params.destColumnName &&
      NON_SORTABLE_PIPELINE_COLUMNS.has(params.destColumnName)
    ) {
      return;
    }

    if (
      params.destColumnName === ConsultantPipelineStatus.NotRelevant &&
      params.sourceColumnName !== ConsultantPipelineStatus.NotRelevant
    ) {
      const card = getCardById(params.sourceColumnId, params.cardId);

      if (card) {
        handleChangeUntagConsultantId(card.issue.consultant._id, "column");
      }

      return;
    }

    if (params.sourceColumnName === ConsultantPipelineStatus.RelevantInTheFuture) {
      if (params.destColumnName === ConsultantPipelineStatus.RelevantInTheFuture) {
        return;
      }

      const card = getCardById(params.sourceColumnId, params.cardId);
      const consultantId = card.issue.consultant._id;

      // Optimistic move out of "Aktuell längre fram": assume success, revert
      // on error. Matches the pattern used by the plain moveCard path below.
      dispatch({
        type: "MOVE_CARD",
        payload: {
          cardId: params.cardId,
          destColumnId: params.destColumnId,
          destPosition: params.destPosition
        }
      });

      // Clear consultant.nextContactDate immediately so the card colour and
      // "Nästa kontakt" label update right away (CRMDEV-2644: colour is
      // driven by nextContactDate, not by column). Without this the card
      // stays yellow with the stale date visible until the originator-skip
      // window expires (recentLocalMoves blocks the subscription patch that
      // would otherwise refresh the data).
      const hadNextContactDate = !!card.issue.consultant?.nextContactDate;
      if (hadNextContactDate) {
        dispatch({
          type: "CLEAR_CARD_NEXT_CONTACT_DATE",
          payload: { cardId: params.cardId }
        });
      }

      recentLocalMoves.current.set(params.cardId, Date.now());

      unsetNextContactDate({
        variables: {
          consultantId,
          nextPipelineStatus: params.destColumnName
        },
        update: (cache) => {
          // Keep the Apollo cache in sync with the move so the next
          // kanbanCards re-emit doesn't snap this card back to its old
          // column. We also write the optimistic synthOrder (set by Fix 1
          // in move-card.ts and read here via stateRef) so the re-emit
          // doesn't displace the card based on its STALE pre-move cached
          // order — without that, the card would land wherever its old
          // order falls among the dest column's other (also stale) orders,
          // typically the end of the column.
          const synthOrder = readOptimisticOrder(params.cardId);
          cache.modify({
            id: cache.identify({
              __typename: "ConsultantPipelineCard",
              _id: params.cardId
            }),
            fields: {
              columnId: () => params.destColumnName,
              ...(synthOrder !== undefined && { order: () => synthOrder })
            }
          });
          cache.modify({
            id: cache.identify({
              __typename: "Consultant",
              _id: consultantId
            }),
            fields: {
              nextContactDate: () => null,
              pipelineStatus: () => params.destColumnName
            }
          });
        }
      }).catch(() => {
        showError("Failed to move card");
        dispatch({
          type: "MOVE_CARD",
          payload: {
            cardId: params.cardId,
            destColumnId: params.sourceColumnId,
            destPosition: params.sourcePosition
          }
        });
        refetch();
      });

      return;
    }

    if (params.destColumnName === ConsultantPipelineStatus.RelevantInTheFuture) {
      const card = getCardById(params.sourceColumnId, params.cardId);
      if (card) handleChangeConsultantRelevantLater(card.issue.consultant._id);

      return;
    }

    recentLocalMoves.current.set(params.cardId, Date.now());

    _moveCard({
      variables: {
        boardId: state?.board?.id,
        boardType: KanbanBoardType.ConsultantPipeline,
        cardId: params.cardId,
        columnId: params.destColumnId,
        beforeCardId: params.destBeforeCardId,
        sessionId: sessionId.current
      },
      update: (cache) => {
        // Keep the Apollo cache in sync with the move (see equivalent
        // comment on the unsetNextContactDate path above for the full
        // rationale). We also write the optimistic synthOrder so the
        // post-mutation kanbanCards re-emit doesn't sort the card to
        // the end of the column based on its stale cached order.
        const synthOrder = readOptimisticOrder(params.cardId);
        cache.modify({
          id: cache.identify({
            __typename: "ConsultantPipelineCard",
            _id: params.cardId
          }),
          fields: {
            columnId: () => params.destColumnId,
            ...(synthOrder !== undefined && { order: () => synthOrder })
          }
        });
      }
    })
      .catch(() => {
        showError("Failed to move card");
        dispatch({
          type: "MOVE_CARD",
          payload: {
            cardId: params.cardId,
            destColumnId: params.sourceColumnId,
            destPosition: params.sourcePosition
          }
        });
        recentLocalMoves.current.delete(params.cardId);
      })
      .finally(() => {
        if (params.destColumnName === ConsultantPipelineStatus.ReadyToBePresented) {
          fireworks?.shoot();
        }
      });

    // Compute the optimistic destination array index from beforeCardId
    // rather than trusting the kanban library's `destPosition`. Background:
    // for some "in-between" drops within the same column the library returns
    // a destPosition that equals the card's current source index, so its
    // `_moveCard` helper sees a same-position move and returns state
    // unchanged. Since the originator now skips the subscription patch
    // (see patchSingleCard), nothing else updates the visible state and
    // the move only becomes visible after a manual refresh. By targeting
    // beforeCard's current array index directly we ensure the splice does
    // something meaningful, matching the backend's "place just before
    // beforeCardId" semantics. See CRMDEV-6787 follow-up.
    let optimisticDestPosition = params.destPosition;
    const destCol = state.board?.columns.find(
      (c) => c.id === params.destColumnId
    );
    if (params.destBeforeCardId && destCol) {
      const beforeIdx = destCol.cards.findIndex(
        (c) => c.id === params.destBeforeCardId
      );
      if (beforeIdx >= 0) {
        optimisticDestPosition = beforeIdx;
      } else {
        optimisticDestPosition = destCol.cards.length;
      }
    } else if (!params.destBeforeCardId && destCol) {
      optimisticDestPosition = destCol.cards.length;
    }

    dispatch({
      type: "MOVE_CARD",
      payload: {
        cardId: params.cardId,
        destColumnId: params.destColumnId,
        destPosition: optimisticDestPosition
      }
    });
  };

  const [loadSingleCard] = useGetConsultantPipelineCardLazyQuery({
    fetchPolicy: "network-only"
  });

  const patchSingleCard = async (cardId: string) => {
    if (!state?.board?.id || !cardId) return;
    // Skip patches for cards we just moved ourselves. The optimistic
    // MOVE_CARD dispatch has already placed the card at the dropped
    // position; the notifyOnConsultantUpdated republish (with
    // sessionId="") would otherwise overwrite that with the moved card's
    // new `order` while leaving its neighbours' cached orders stale —
    // visually drifting the card to the wrong slot a couple of seconds
    // later. See CRMDEV-6787 follow-up.
    const localMoveTs = recentLocalMoves.current.get(cardId);
    if (localMoveTs && Date.now() - localMoveTs < RECENT_LOCAL_MOVE_WINDOW_MS) {
      traceLocalMove("patchSingleCard:skipped-by-window", cardId);
      return;
    }
    // Old entries are pruned lazily so the map can't grow without bound.
    if (recentLocalMoves.current.size > 50) {
      const cutoff = Date.now() - RECENT_LOCAL_MOVE_WINDOW_MS;
      for (const [k, ts] of recentLocalMoves.current) {
        if (ts < cutoff) recentLocalMoves.current.delete(k);
      }
    }
    const result = await loadSingleCard({
      variables: { boardId: state.board.id, cardId }
    });
    const card = result.data?.kanbanCard as ConsultantPipelineCardFragment | null;
    if (card) {
      traceLocalMove("patchSingleCard:UPSERT_CARD", cardId, {
        columnId: card.columnId,
        order: card.order
      });
      dispatch({ type: "UPSERT_CARD", payload: { card } });
    } else {
      // Network returned no card — would dispatch REMOVE_CARD_BY_ID. If this
      // happens for a card we just moved ourselves it's the disappear bug.
      traceLocalMove("patchSingleCard:REMOVE_CARD_BY_ID(null-data)", cardId);
      dispatch({ type: "REMOVE_CARD_BY_ID", payload: { cardId } });
    }
  };

  // Subscriptions patch the affected card in-place instead of refetching the
  // whole board. The board structure (columns) is stable, so a single-card
  // fetch is sufficient to reflect remote changes. See CRMDEV-6787.
  useCardMovedSubscription({
    variables: { boardId: state?.board?.id ?? "", sessionId: sessionId.current },
    skip: !state?.board,
    onSubscriptionData: ({ subscriptionData }) => {
      const cardId = subscriptionData.data?.kanbanCardMoved?.cardId;
      if (cardId) {
        traceLocalMove("subscription:kanbanCardMoved", cardId);
        void patchSingleCard(cardId);
      }
    }
  });

  useCardAddedSubscription({
    variables: { boardId: state?.board?.id ?? "", sessionId: sessionId.current },
    skip: !state?.board,
    onSubscriptionData: ({ subscriptionData }) => {
      const cardId = subscriptionData.data?.kanbanCardAdded?.cardId;
      if (cardId) {
        traceLocalMove("subscription:kanbanCardAdded", cardId);
        void patchSingleCard(cardId);
      }
    }
  });

  useCardDeletedSubscription({
    variables: { boardId: state?.board?.id ?? "", sessionId: sessionId.current },
    skip: !state?.board,
    onSubscriptionData: ({ subscriptionData }) => {
      const cardId = subscriptionData.data?.kanbanCardDeleted?.cardId;
      if (!cardId) return;
      // Route through patchSingleCard rather than dispatching
      // REMOVE_CARD_BY_ID blindly. Backend's ConsultantEntitySubscriber
      // always populates `responsiblePerson.prev` (regardless of whether
      // it actually changed), and `notifyOnConsultantUpdated` publishes a
      // KanbanCardDeletedEvent whenever that field is truthy. The result
      // is that *every* consultant edit — including pure nextContactDate
      // updates via the dialog menu — emits a CardDeleted event for the
      // originator. Without this routing, editing "Nästa kontakt" on a
      // card in "Aktuell längre fram" makes the card vanish from the
      // board until reload (CRMDEV-2644 follow-up).
      //
      // patchSingleCard fetches the card via network-only and only
      // dispatches REMOVE_CARD_BY_ID when the server actually returns
      // null (true deletion). For a false-positive CardDeleted, the
      // fetch returns the card and we UPSERT it with fresh data — which
      // is what we want anyway, since the originating mutation modified
      // the consultant. The recentLocalMoves window inside
      // patchSingleCard also still protects the originator's own moves.
      traceLocalMove("subscription:kanbanCardDeleted", cardId);
      void patchSingleCard(cardId);
    }
  });

  const handleChangeBoardUser = (userId: string) => {
    dispatch({
      type: "CHANGE_BOARD_USER",
      payload: {
        filter: defaultFilter as ConsultantFilterState,
        userId: userId
      }
    });
  };

  const handleChangeUntagConsultantId = (
    consultantId: string,
    type: "button" | "column"
  ) => {
    dispatch({
      type: "CHANGE_UNTAG_CONSULTANT_ID",
      payload: {
        consultantId,
        questionText:
          type === "button"
            ? "Vill du verkligen tagga av dig själv som Ansvarig HBA?"
            : "Vill du tagga av dig själv som ansvarig HBA så att andra får möjlighet att ringa på denna konsult?"
      }
    });
  };

  const handleChangeConsultantRelevantLater = (consultantId: string) => {
    dispatch({
      type: "CHANGE_CONSULTANT_RELEVANT_LATER",
      payload: {
        consultantId
      }
    });
  };

  const getCardById = (
    columnId: string,
    cardId: string
  ): CardDescription<ConsultantPipelineCardFragment | null> => {
    const column = state.board.columns.find((x) => x.id === columnId);

    if (!column) {
      return null;
    }

    const card = column.cards.find((c) => c.id === cardId);

    if (!card) {
      return null;
    }

    return card;
  };

  return (
    <ConsultantPipelineBoardContext.Provider
      value={{
        state,
        loading,
        error,
        filterManager: filterManager,
        refetch,
        refetchCards: loadCards,
        setUntagConsultantId: (id) => handleChangeUntagConsultantId(id, "button"),
        setFireworksConductor: (conductor) => setFireworks(conductor),
        setBoardUserId: handleChangeBoardUser,
        setConsultantRelevantLaterId: handleChangeConsultantRelevantLater,
        dispatch,
        onMoveCard
      }}
    >
      {children}
    </ConsultantPipelineBoardContext.Provider>
  );
};
